import React, { FC, useContext, useState, useCallback } from "react";
import { IonButton, IonContent } from "@ionic/react";
import clsx from "clsx";

import { Context } from "../../../store/teacher";
import {
  CreateGearboxParams,
  UpdateGearboxParams,
  CreateGearboxTemplateParams,
  UpdateGearboxTemplateParams,
} from "../../../state";
import {
  Menu,
  State,
  BoxState,
  TemplateState,
} from "../../../pages/teacher/ManageBoxesPage/types";
import commonStyles from "../../../pages/teacher/common.module.scss";
import EmbeddedHeader from "../../../organisms/teacher/Header/EmbeddedHeader";

import ManageBoxesMenu from "./ManageBoxesMenu";
import ManageTemplatesMenu from "./ManageTemplatesMenu";
import BoxForm from "./BoxForm";
import TemplateForm from "./TemplateForm";
import CreateButton from "./CreateButton";
import ListSelector from "./ListSelector";
import styles from "./ManageMenu.module.scss";

type Props = {
  title: string;
  calling: boolean;
  menu: Menu;
  setMenu: React.Dispatch<React.SetStateAction<Menu>>;
  state: State;
  setState: React.Dispatch<React.SetStateAction<State>>;
  createGearbox: (args: CreateGearboxParams) => void;
  updateGearbox: (args: UpdateGearboxParams) => void;
  deleteGearbox: (id: number) => void;
  gearboxHidden: (id: number) => void;
  createTemplate: (args: CreateGearboxTemplateParams) => void;
  updateTemplate: (args: UpdateGearboxTemplateParams) => void;
  deleteTemplate: (id: number) => void;
  edit: () => void;
};

const ManageMenu: FC<Props> = ({
  title,
  calling,
  menu,
  setMenu,
  state,
  setState,
  createGearbox,
  updateGearbox,
  deleteGearbox,
  gearboxHidden,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  edit,
}) => {
  const { contextState } = useContext(Context);

  const [boxState, setBoxState] = useState<BoxState>({
    teacherGearboxDirectories: [],
    showBoxForm: false,
    showDeleteBoxConfirm: false,
    gearboxID: 0,
    teacherGearboxDirectoryID: 0,
    gearboxTitle: "",
    gearboxTemplateId: null,
    classID: 0,
    nameStatus: "publish",
    is_archived: false,
  });
  const [templateState, setTemplateState] = useState<TemplateState>({
    showTemplateForm: false,
    showDeleteTemplateConfirm: false,
    templateId: null,
    templateTitle: "",
    templateDescription: "",
    templateContent: "",
  });

  const handleSaveTemplate = (
    template: CreateGearboxTemplateParams | UpdateGearboxTemplateParams,
  ) => {
    if ("id" in template && template.id !== 0) {
      updateTemplate(template);
    } else {
      createTemplate(template);
    }
    setMenu("template");
    setTemplateState(s => ({
      ...s,
      showTemplateForm: false,
    }));
  };

  const update = useCallback(
    (args: { [key: string]: any }) =>
      setState(v => ({
        ...v,
        ...args,
      })),
    [setState],
  );

  const handleChangeMenu = (menu: Menu) => {
    setMenu(menu);
    setState(prev => ({ ...prev, editable: false }));
  };

  return (
    <>
      <IonContent
        className={clsx(commonStyles.wrapper, commonStyles.bgWhileBase)}
      >
        <div className={commonStyles.container}>
          <EmbeddedHeader
            title={title}
            rightComponent={
              <IonButton
                className={styles.editButton}
                fill="clear"
                onClick={edit}
              >
                <p className={styles.editButtonTitle}>
                  {state.editable ? "完了" : "編集"}
                </p>
              </IonButton>
            }
          />
          <div className={styles.content}>
            <div className={styles.createBlock}>
              <CreateButton
                setBoxState={setBoxState}
                setTemplateState={setTemplateState}
                update={update}
              />
            </div>
            <div>
              <ListSelector menu={menu} onChangeMenu={handleChangeMenu} />
            </div>
            {menu === "box" && (
              <ManageBoxesMenu
                calling={calling}
                editable={state.editable}
                teacher={contextState.teacher}
                teacherGearboxDirectories={
                  contextState.teacherGearboxDirectories
                }
                gearboxes={contextState.gearboxes}
                state={boxState}
                setState={setBoxState}
                gearboxHidden={gearboxHidden}
                templates={[
                  ...contextState.my_gearbox_templates,
                  ...contextState.shared_gearbox_templates,
                ]}
                createTemplate={() => {
                  handleChangeMenu("template");
                }}
                createGearbox={createGearbox}
                deleteGearbox={deleteGearbox}
                updateGearbox={updateGearbox}
                update={update}
              />
            )}
            {menu === "template" && (
              <ManageTemplatesMenu
                calling={calling}
                editable={state.editable}
                teacher={contextState.teacher}
                templates={contextState.my_gearbox_templates}
                state={templateState}
                setState={setTemplateState}
                createTemplate={createTemplate}
                updateTemplate={updateTemplate}
                deleteTemplate={deleteTemplate}
                changeMenu={menu => handleChangeMenu(menu)}
                update={update}
              />
            )}
          </div>
        </div>
      </IonContent>
      <BoxForm
        calling={calling}
        gearboxes={contextState.gearboxes}
        isOpen={boxState.showBoxForm}
        classes={contextState.teacher.classes}
        teacherGearboxDirectories={contextState.teacherGearboxDirectories}
        defaultTitle={boxState.gearboxTitle}
        defaultTemplateId={boxState.gearboxTemplateId}
        id={boxState.gearboxID}
        classID={boxState.classID}
        teacherGearboxDirectoryID={boxState.teacherGearboxDirectoryID}
        nameStatus={boxState.nameStatus}
        is_archived={boxState.is_archived}
        templates={[
          ...contextState.my_gearbox_templates,
          ...contextState.shared_gearbox_templates,
        ]}
        ok={gearbox => {
          if (boxState.gearboxID) {
            updateGearbox({
              id: gearbox.id,
              title: gearbox.title,
              teacher_gearbox_directory_id:
                gearbox.teacher_gearbox_directory_id,
              name_status: gearbox.name_status,
              is_archived: gearbox.is_archived,
              gearbox_template_id: gearbox.gearbox_template_id,
            });
          } else {
            createGearbox({
              title: gearbox.title,
              class_ids: [gearbox.class_id],
              teacher_gearbox_directory_id:
                gearbox.teacher_gearbox_directory_id,
              name_status: gearbox.name_status,
              is_archived: gearbox.is_archived,
              gearbox_template_id: gearbox.gearbox_template_id,
            });
            setMenu("box");
          }
          setBoxState(s => ({
            ...s,
            showBoxForm: false,
          }));
        }}
        close={() => {
          /*
            setBoxStateの多重実行防止
            別の箇所からshowBoxForm: falseにしたタイミングで動的に実行されてしまうため
            BoxForm内で利用しているIonModalのonDidDismissからcloseを参照しているのが原因っぽい
          */
          if (boxState.showBoxForm) {
            setBoxState(s => ({ ...s, showBoxForm: false }));
          }
        }}
      />
      <TemplateForm
        calling={calling}
        isOpen={templateState.showTemplateForm}
        id={templateState.templateId}
        defaultTitle={templateState.templateTitle}
        defaultDescription={templateState.templateDescription}
        defaultContent={templateState.templateContent}
        ok={template => handleSaveTemplate(template)}
        close={() => setTemplateState(s => ({ ...s, showTemplateForm: false }))}
      />
    </>
  );
};

export default ManageMenu;
